import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiClock, FiEdit2 } from "react-icons/fi";
import { SupplierInformationDto, SeV2QuestionDto, SeV2QuestionMessageDto } from "../../../../../autogen/bff-api";
import { AttachmentList } from "../../../../../common/components/communication/AttachmentList";
import {
  formatDateTime,
  personName,
  richTextEquals,
  richTextHasContent,
} from "../../../../../common/components/communication/communicationUtils";
import { QuestionStatusChip } from "../../../../../common/components/communication/QuestionStatusChip";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";

const DialogMessage = ({
  message,
  askedByOrgName,
}: {
  message: SeV2QuestionMessageDto;
  askedByOrgName: string;
}) => {
  const isBuyerSide = message.sentByOrgName !== askedByOrgName;
  return (
    <Box
      px="4"
      py="3"
      borderTopWidth="1px"
      borderColor="smBorder"
      bg={isBuyerSide ? "smPrimarySofter" : undefined}
    >
      <Text fontSize="sm" fontWeight="semibold" color={isBuyerSide ? "smChipTealFg" : "smText"} mb="1">
        {personName(message.sentBy)} · {message.sentByOrgName}{" "}
        <Text as="span" fontWeight="normal" color="smMuted">
          · {formatDateTime(message.sentAt)}
        </Text>
      </Text>
      <TipTap content={message.content} editable={false} maxHeight="48" />
    </Box>
  );
};

export const SupplierQuestionThread = ({
  question,
  publishedAnswer,
}: {
  question: SeV2QuestionDto;
  publishedAnswer?: SupplierInformationDto;
}) => {
  const { t } = useTranslation();
  const directAnswer = question.answer;
  // When the buyer publishes the question to all tenderers they may reword it (e.g. to
  // anonymize the supplier). Show that edited version alongside the original so the asker
  // can see how their question was published — but only when it actually differs.
  const editedQuestion = publishedAnswer?.question ?? undefined;
  const showEditedQuestion =
    !!editedQuestion &&
    richTextHasContent(editedQuestion) &&
    !richTextEquals(editedQuestion, question.content);
  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="lg" overflow="hidden">
      <HStack
        px="4"
        py="3"
        borderBottomWidth="1px"
        borderColor="smBorder"
        justify="space-between"
        align="center"
        spacing="3"
      >
        <Text fontSize="md" fontWeight="semibold" color="smText" minW={0} noOfLines={1}>
          {question.subject}
        </Text>
        <QuestionStatusChip status={question.status} />
      </HStack>

      <Box px="4" py="3">
        <Text fontSize="sm" fontWeight="semibold" color="smText" mb="1">
          {t("Your question")}{" "}
          <Text as="span" fontWeight="normal" color="smMuted">
            · {formatDateTime(question.createdAt)}
            {showEditedQuestion && <> · {t("only shown to you")}</>}
          </Text>
        </Text>
        <TipTap content={question.content} editable={false} maxHeight="96" />
        <AttachmentList eventId={question.eventId} attachments={question.attachments} />
      </Box>

      {showEditedQuestion && editedQuestion && (
        <Box px="4" py="3" borderTopWidth="1px" borderColor="smBorder">
          <HStack spacing="1.5" mb="1">
            <Icon as={FiEdit2} boxSize="3.5" color="smMuted" />
            <Text fontSize="sm" fontWeight="semibold" color="smText">
              {t("Edited by the buyer")}{" "}
              <Text as="span" fontWeight="normal" color="smMuted">
                · {t("shown to all tenderers")}
              </Text>
            </Text>
          </HStack>
          <TipTap content={editedQuestion} editable={false} maxHeight="96" />
        </Box>
      )}

      {question.messages && question.messages.length > 0 &&
        question.messages.map((msg, index) => (
          <DialogMessage key={`${msg.sentAt}-${index}`} message={msg} askedByOrgName={question.askedByOrgName} />
        ))}

      {publishedAnswer && (
        <Box px="4" py="4" borderTopWidth="1px" borderColor="smBorder" bg="smPrimarySofter">
          <Text fontSize="sm" fontWeight="semibold" color="smChipTealFg" mb="1">
            {t("Answer from the buyer")}{" "}
            <Text as="span" fontWeight="normal" color="smMuted">
              · {formatDateTime(publishedAnswer.createdAt)}
            </Text>
          </Text>
          <TipTap content={publishedAnswer.content} editable={false} maxHeight="96" />
          <AttachmentList eventId={question.eventId} attachments={publishedAnswer.attachments} />
        </Box>
      )}

      {!publishedAnswer && directAnswer && (
        <Box px="4" py="4" borderTopWidth="1px" borderColor="smBorder" bg="smPrimarySofter">
          <Text fontSize="sm" fontWeight="semibold" color="smChipTealFg" mb="1">
            {t("Answer from the buyer")}{" "}
            <Text as="span" fontWeight="normal" color="smMuted">
              · {formatDateTime(directAnswer.answeredAt)}
            </Text>
          </Text>
          <TipTap content={directAnswer.content} editable={false} maxHeight="96" />
          <AttachmentList eventId={question.eventId} attachments={directAnswer.attachments} />
        </Box>
      )}

      {!publishedAnswer && !directAnswer && (
        <HStack
          px="4"
          py="3"
          borderTopWidth="1px"
          borderColor="smBorder"
          spacing="2"
          color="smMuted"
        >
          <Icon as={FiClock} boxSize="4" />
          <Text fontSize="sm">{t("Waiting for an answer from the buyer")}</Text>
        </HStack>
      )}
    </Box>
  );
};
